import type { AddressResponse } from "@loanlink-nl/model-app";
import { DateTime, Duration } from "luxon";
import { fuzzyDateParse } from "@loanlink-nl/migration";

export function getTextColorFromBackground(
	r: number,
	g: number,
	b: number,
): [number, number, number] {
	// https://ux.stackexchange.com/a/8320
	const grayScaleBrightness =
		0.2126 * Math.pow(r / 255, 2.2) +
		0.7151 * Math.pow(g / 255, 2.2) +
		0.0721 * Math.pow(b / 255, 2.2);

	return grayScaleBrightness <= 0.18 ? [255, 255, 255] : [0, 0, 0];
}

export async function stringToRgb(
	str: string,
): Promise<[number, number, number]> {
	const encoder = new TextEncoder();
	const data = encoder.encode(str);
	const hash = await window.crypto.subtle.digest("SHA-1", data);

	const [h, s, l] = arrayBufferToHSLColor(hash);

	const rgb = hslToRgb(h / 360, s / 100, l / 100);

	return rgb;
}

/**
 * Convert an ArrayBuffer to an HSL color string, clamping sat & light ranges.
 *
 * @param {ArrayBuffer} buffer - At least 6 bytes.
 * @returns CSS color string in the form "hsl(H, S%, L%)"
 */
function arrayBufferToHSLColor(buffer: ArrayBuffer): [number, number, number] {
	// We need a minimum of 6 bytes to extract meaningful H, S, L
	const bytes = new Uint8Array(buffer);
	if (bytes.length < 6) {
		throw new Error("Not enough data in ArrayBuffer (need >= 6 bytes).");
	}

	// 1. HUE: interpret bytes [0,1] as a 16-bit integer, scale to [0..360)
	const hueRaw = (bytes[0]! << 8) | bytes[1]!; // 0..65535
	const hue = (hueRaw / 65535) * 360; // 0..360

	// Bytes [2,3] -> Saturation fraction -> map to [40..80%]
	const satRaw = (bytes[2]! << 8) | bytes[3]!;
	const satFraction = satRaw / 65535; // 0..1
	const satMin = 40,
		satMax = 80;
	const saturation = satMin + satFraction * (satMax - satMin); // 40..80

	// Bytes [4,5] -> Lightness fraction -> map to [20..40%]
	const lightRaw = (bytes[4]! << 8) | bytes[5]!;
	const lightFraction = lightRaw / 65535;
	const lightMin = 40,
		lightMax = 60;
	const lightness = lightMin + lightFraction * (lightMax - lightMin); // 20..40

	// Round or keep decimals if you prefer
	const h = Math.round(hue);
	const s = Math.round(saturation);
	const l = Math.round(lightness);

	return [h, s, l];
}

/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from https://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * https://stackoverflow.com/a/9493060/9487833
 *
 * @param   {number}  h       The hue
 * @param   {number}  s       The saturation
 * @param   {number}  l       The lightness
 * @return  {Array}           The RGB representation
 */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
	let r: number, g: number, b: number;

	if (s === 0) {
		r = g = b = l; // achromatic
	} else {
		const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
		const p = 2 * l - q;
		r = hueToRgb(p, q, h + 1 / 3);
		g = hueToRgb(p, q, h);
		b = hueToRgb(p, q, h - 1 / 3);
	}

	return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function hueToRgb(p: number, q: number, t: number) {
	if (t < 0) t += 1;
	if (t > 1) t -= 1;
	if (t < 1 / 6) return p + (q - p) * 6 * t;
	if (t < 1 / 2) return q;
	if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
	return p;
}

export function getDisplayDate(date: string | undefined) {
	const parsedDate = fuzzyDateParse(date);
	if (!parsedDate.isValid) {
		return "";
	}

	return parsedDate.setLocale("nl-NL").toLocaleString(DateTime.DATE_HUGE);
}

/**
 * Format seconds into a readable time format (mm:ss or hh:mm:ss)
 */
export function formatTimestamp(totalSeconds: number): string {
	// Create a Duration from seconds and shift it to hours, minutes, and seconds.
	let duration = Duration.fromObject({ seconds: totalSeconds }).shiftTo(
		"hours",
		"minutes",
		"seconds",
	);

	// Floor each unit to remove any fractional parts (in case totalSeconds is not an integer).
	duration = duration.mapUnits(Math.floor);

	// Check if hours are zero, then format accordingly
	if (duration.hours < 1) {
		// If no hours, format as "mm:ss"
		return duration.toFormat("mm:ss");
	}
	// If hours exist, format as "hh:mm:ss"
	return duration.toFormat("hh:mm:ss");
}

export function simpleDate(date: string | undefined) {
	const parsedDate = fuzzyDateParse(date);
	if (!parsedDate.isValid) {
		return "";
	}

	return parsedDate
		.setLocale("nl-NL")
		.toLocaleString({ day: "2-digit", month: "2-digit", year: "numeric" });
}

export function formatDate(value?: string | Date | number | null) {
	if (value === undefined || value === null) {
		return "";
	}

	const date = new Date(value);

	const dateFormatConfig: Intl.DateTimeFormatOptions = {
		hour: "numeric",
		minute: "numeric",
	};

	const today = new Date();

	const isToday = today.toDateString() === date.toDateString();
	if (!isToday) {
		dateFormatConfig.month = "short";
		dateFormatConfig.day = "numeric";
	}

	const isThisYear = today.getFullYear() === date.getFullYear();
	if (!isThisYear) {
		dateFormatConfig.year = "numeric";
	}

	return date.toLocaleString("nl-NL", dateFormatConfig);
}

export function getHuisnummer(result?: AddressResponse) {
	if (!result) return "";

	let label: string = String(result.huisnummer);
	if (result.huisletter) {
		label += result.huisletter;
	}

	if (result.huisnummertoevoeging) {
		label += result.huisnummertoevoeging;
	}

	return label;
}

export function addressLine5(result?: AddressResponse) {
	if (!result) return "";

	let label = "";
	if (result.korteNaam) {
		label += result.korteNaam;
	} else if (result.openbareRuimteNaam) {
		label += result.openbareRuimteNaam;
	}

	if (result.huisnummer) {
		label += ` ${result.huisnummer}`;
	}

	if (result.huisletter || result.huisnummertoevoeging) {
		label += `-`;

		if (result.huisletter) {
			label += `${result.huisletter}`;
		}

		if (result.huisnummertoevoeging) {
			label += `${result.huisnummertoevoeging}`;
		}
	}

	return label;
}

export function addressLine6(result?: AddressResponse) {
	if (!result) return "";

	let label = "";
	if (result.postcode) {
		label += result.postcode;
	}

	if (result.woonplaatsNaam) {
		if (label.length > 0) {
			label += " ";
		}
		label += result.woonplaatsNaam;
	}
	return label;
}

export function addressToLabel(result?: AddressResponse) {
	if (!result) return "";

	const line5 = addressLine5(result);
	const line6 = addressLine6(result);

	let label = line5;
	if (line6) {
		label += `, ${line6}`;
	}

	return label;
}

export function stringifyObject(json: any) {
	return JSON.stringify(json, undefined, 2);
}
